import React, { FC, useMemo, useState } from 'react';

import { Formik } from 'formik';
import * as Yup from 'yup';

import { useToast } from 'Shared/components';
import { useLazyQuoteApi } from 'Shared/services/Api';

import {
  AccidentDataType,
  CommandType,
  GetDetailsResponseType,
  transformAppDetails,
} from 'QuoteApp/services/endpoints/GetDetails';

import {
  AccidentChallengeFormValuesType,
  transformAccidentChallengeRequest,
} from 'QuoteApp/services/endpoints/PostChallengeAccident';
import { useAppContext } from 'QuoteApp/services/providers';
import { getErrorMessage } from 'QuoteApp/utils';
import { useEdit } from 'QuoteApp/utils/contexts';
import {
  getReportMessages,
  ReportChangeMessageType,
  ReportMessages,
} from 'QuoteApp/screens/Reports';
import { ChallengeForm } from '../../../ChallengeForm';
import { ReportDetailRow } from '../../../ReportDetailRow';
import styles from './styles.module.css';
import { useQuoteAppFeatureFlags } from 'QuoteApp/utils/hooks/useQuoteAppFeatureFlags';
import { AxiosPromise, AxiosRequestConfig } from 'axios';
import { RefetchOptions } from 'axios-hooks';
import { RateType } from 'QuoteApp/services/endpoints/GetRate';
import { ToastOptions } from 'Shared/components/Toast/Toast';
import { AccidentChallengeForm } from 'QuoteApp/components/ReportDetails/AccidentChallengeForm';
import { buildAvailableDrivers } from 'QuoteApp/components/ReportDetails/AccidentChallengeForm/buildAvailableDrivers';

type AccidentFormPropsType = {
  driverId: string;
  incidentId: string;
  canChallenge: boolean;
};

export const getChallengeAccidentInitialFormValues = (
  accident: AccidentDataType,
  identifier: string
): AccidentChallengeFormValuesType => {
  return {
    [identifier]: {
      challengeType: 'edit',
      atFault: accident.atFault.value,
      month: accident.month.value,
      day: accident.day.value,
      year: accident.year.value,
      challengeReason: '',
      reasonText: '',
      date: accident.date.value,
      accidentType: accident.accidentType.value,
      lossAmount: accident.lossAmount.value,
      operatorId: accident.operatorId ?? '',
    },
  };
};

export const getChallengeAccidentFormValidation = (
  identifier: string,
  selfReportedAccidentsForClassificationsFlag: boolean,
  collectDayOfAccidentFlag: boolean
) => {
  const baseSchema = {
    challengeType: Yup.string(),
    challengeReason: Yup.string().when('challengeType', {
      is: 'remove',
      then: Yup.string().required('Required'),
      otherwise: Yup.string().notRequired(),
    }),
    date: Yup.string(),
    accidentType: Yup.string(),
    lossAmount: Yup.string(),
  };

  if (collectDayOfAccidentFlag) {
    baseSchema.date = Yup.string().required('Date is required.');
  }

  if (selfReportedAccidentsForClassificationsFlag) {
    baseSchema.accidentType = Yup.string().required('Claim Type is required.');
    baseSchema.lossAmount = Yup.string().required('Loss Amount is required.');
  }

  const schema = {
    [identifier]: Yup.object().shape(baseSchema),
  };

  return schema;
};

export const onSubmitClaimChallenge = async (
  values: AccidentChallengeFormValuesType,
  identifier: string,
  challengeAccident: (
    config?: AxiosRequestConfig<any> | undefined,
    options?: RefetchOptions | undefined
  ) => AxiosPromise<GetDetailsResponseType>,
  challengeCommand: CommandType | undefined,
  fetchRate: () => Promise<RateType | null>,
  rate: RateType | null,
  setMessages: React.Dispatch<React.SetStateAction<ReportChangeMessageType[]>>,
  changeEditingId: (id: string | null) => void,
  setDetails: (newDetails: GetDetailsResponseType) => void,
  toast: (toastOptions: ToastOptions) => void
) => {
  try {
    const response = await challengeAccident({
      method: 'POST',
      url: challengeCommand?.uri?.replace('/api/v3', ''),
      data: values[identifier],
      transformRequest: transformAccidentChallengeRequest,
      transformResponse: transformAppDetails,
    });
    if (response.data.status === 'open') {
      const newRate = await fetchRate();
      rate &&
        newRate &&
        setMessages(
          getReportMessages(
            rate,
            newRate,
            'disputed report data',
            'accident-challenging'
          )
        );
    }
    changeEditingId(null);
    setDetails(response.data);
  } catch (e) {
    toast({
      type: 'error',
      message: getErrorMessage(e),
      withSecondaryNav: false,
      icon: 'error',
    });
  }
};

export const AccidentForm: FC<AccidentFormPropsType> = ({
  driverId,
  incidentId,
  canChallenge,
}) => {
  const { details, config, setDetails, fetchRate, rate } = useAppContext();
  const {
    selfReportedAccidentsForClassificationsFlag,
    collectDayOfAccidentFlag,
  } = useQuoteAppFeatureFlags();
  const { editingId, changeEditingId } = useEdit();
  const { toast } = useToast();
  const identifier = `${incidentId}-accident`;
  const [messages, setMessages] = useState<Array<ReportChangeMessageType>>([]);

  const [, challengeAccident] = useLazyQuoteApi<GetDetailsResponseType>({});

  const accident = details.drivers[driverId].accidents[incidentId];

  const availableDrivers = useMemo(
    () => buildAvailableDrivers(details.drivers),
    [details.drivers]
  );

  const challengeCommand = details.commands.drivers[driverId].accidents[
    incidentId
  ].find((c) => c.name === 'challenge_accident');

  if (!config) return null;

  return (
    <Formik<AccidentChallengeFormValuesType>
      enableReinitialize
      initialValues={getChallengeAccidentInitialFormValues(
        accident,
        identifier
      )}
      validationSchema={Yup.object().shape(
        getChallengeAccidentFormValidation(
          identifier,
          selfReportedAccidentsForClassificationsFlag,
          collectDayOfAccidentFlag
        )
      )}
      onSubmit={async (values) => {
        await onSubmitClaimChallenge(
          values,
          identifier,
          challengeAccident,
          challengeCommand,
          fetchRate,
          rate,
          setMessages,
          changeEditingId,
          setDetails,
          toast
        );
      }}
    >
      {({ values, setFieldValue, errors, resetForm }) => {
        const isEditing =
          editingId === identifier &&
          values[identifier].challengeType === 'edit';

        return (
          <>
            <ReportDetailRow
              identifier={identifier}
              canChallenge={canChallenge}
              challengeCommand={challengeCommand}
              source={accident.atFault.source}
              checkmarked={null}
              claimStatus={accident.claimStatus.value}
              onEdit={() => {
                resetForm();
                setFieldValue(`${identifier}.challengeType`, 'edit');
              }}
              onRemove={() => {
                resetForm();
                setFieldValue(`${identifier}.challengeType`, 'remove');
              }}
              challengeForm={
                <ChallengeForm
                  variant={values[identifier].challengeType}
                  identifier={identifier}
                  type="accidents"
                  challengeReasonConfig={
                    config.drivers.accidents.challengeReasonOptions
                  }
                />
              }
              type="accidents"
              includeEdit={!editingId}
              includeRemove={!editingId}
            >
              <AccidentChallengeForm
                accident={accident}
                identifier={identifier}
                values={values}
                isEditing={isEditing}
                setFieldValue={setFieldValue}
                errors={errors}
                availableDrivers={availableDrivers}
              />
            </ReportDetailRow>
            <ReportMessages messages={messages} className={styles.message} />
          </>
        );
      }}
    </Formik>
  );
};
